import { getApiUrl } from '@/utils/apiConfig';
import { create } from 'zustand';
import { majorParties, otherParties, allPartiesIncludingNonParliamentary } from "./parties";
import { encodeWafPayload } from "@/utils/apiUtils";

export type PartyProfileStatus = "non_defini" | "en_attente" | "valide" | "rejete";

export interface PartyPoster {
  url: string;
  titleFr: string;
  titleAr: string;
  descriptionFr?: string;
  descriptionAr?: string;
  isActive: boolean;
}

export interface PartyProfile {
  acronym: string;
  fullNameFr: string;
  fullNameAr: string;
  fullNameMz?: string;
  focusFr: string;
  focusAr: string;
  focusMz?: string;
  logoUrl: string;
  fullPosterUrl?: string;
  poster?: PartyPoster;
  programmePdfUrl?: string;
  isProgrammePublished?: boolean;
  presentationUrl?: string;
  programUrl?: string;
  jeunesseUrl?: string;
  femmesUrl?: string;
  academieUrl?: string;
  adhesionUrl?: string;
  partyPrinciples?: { title: string; description: string; readMoreUrl?: string }[];
  website: string;
  socials: {
    facebook?: string;
    twitter?: string;
    instagram?: string;
    youtube?: string;
  };
  status: PartyProfileStatus;
  submittedAt?: string;
  validatedAt?: string;
  validatedBy?: string;
  adminFeedback?: { message: string, date: string, type: 'remarque' | 'validation' | 'rejet' }[];
  isCustom?: boolean;
  color?: string;
  
  modificationsCount?: number;
  adminAuthorization?: boolean;
  lastJustification?: string;
  previousProfileData?: Partial<PartyProfile>;
  
  unlockRequestStatus?: "pending" | "approved" | "rejected" | null;
  unlockRequestSubject?: string;
  unlockRequestMessage?: string;
  unlockRequestFeedback?: string;
}

export interface PosterFormCustomParty {
  acronym: string;
  fullNameFr: string;
  fullNameAr: string;
  color: string;
}

interface PartiesStoreState {
  profiles: PartyProfile[];
  fetchProfiles: () => Promise<void>;
  saveProfile: (profile: PartyProfile) => Promise<boolean>;
  submitProfileModification: (profile: PartyProfile, justification: string) => Promise<boolean>;
  adminValidateProfile: (acronym: string, status: "valide" | "rejete", adminName: string, message?: string) => Promise<void>;
  adminBlockProfileModifications: (acronym: string) => Promise<void>;
  adminAuthorizeProfileModification: (acronym: string) => Promise<void>;
  adminSendProfileMessage: (acronym: string, message: string) => Promise<void>;
  requestProfileUnlock: (acronym: string, subject: string, message: string) => Promise<void>;
  moderateProfileUnlock: (acronym: string, status: "approved" | "rejected", feedback: string) => Promise<void>;
}

export const usePartiesStore = create<PartiesStoreState>((set, get) => ({
  profiles: [],
  
  fetchProfiles: async () => {
    try {
      const res = await fetch(`${getApiUrl()}/parties.php?t=${Date.now()}`);
      if (!res.ok) throw new Error("API Error");
      const apiProfiles = await res.json();
      const apiProfilesArray = Array.isArray(apiProfiles) ? apiProfiles : [];
      
      const mergedProfiles = allPartiesIncludingNonParliamentary.map(staticParty => {
        const apiData = apiProfilesArray.find((p: any) => p.acronym === staticParty.acronym);
        if (apiData) return apiData;
        
        return {
          acronym: staticParty.acronym,
          fullNameFr: staticParty.name || "",
          fullNameAr: staticParty.acronymAr || "",
          fullNameMz: (staticParty as any).nameMz || "",
          focusFr: "",
          focusAr: "",
          focusMz: "",
          logoUrl: staticParty.logoUrl || "",
          website: staticParty.website || "",
          socials: staticParty.socials || {},
          status: "non_defini",
          color: staticParty.color,
          isCustom: false
        } as PartyProfile;
      });

      apiProfilesArray.forEach((apiP: any) => {
        if (!mergedProfiles.some(p => p.acronym === apiP.acronym)) {
          mergedProfiles.push(apiP);
        }
      });

      set({ profiles: mergedProfiles });
    } catch (e) {
      console.error("Failed to fetch party profiles", e);
    }
  },

  saveProfile: async (profile): Promise<boolean> => {
    try {
      const token = localStorage.getItem('kaynin_auth_token');
      
      // Omit previousProfileData to prevent WAF from blocking huge JSON objects
      const { previousProfileData, ...profileToSend } = profile;
      
      // Base64 encode the payload to bypass aggressive WAF rules on Hostinger
      const encodedPayload = encodeWafPayload({ action: 'save', profile: profileToSend });

      const res = await fetch(`${getApiUrl()}/parties.php`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'text/plain',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: encodedPayload
      });
      
      if (!res.ok) {
        let errText = await res.text();
        try {
          const errJson = JSON.parse(errText);
          if (errJson.error) errText = errJson.error;
        } catch (e) {
          // ignore
        }
        console.error("Server returned error:", res.status, errText);
        alert(`Erreur de sauvegarde: ${res.status} - ${errText}`);
        return false;
      }

      // Verify the response is actually from our API and not a WAF interception
      const responseText = await res.text();
      try {
        const jsonResponse = JSON.parse(responseText);
        if (jsonResponse.error) {
          alert(`Erreur du serveur: ${jsonResponse.error}`);
          return false;
        }
      } catch (e) {
        console.error("WAF interception or invalid response:", responseText);
        alert("La sauvegarde a été bloquée par le pare-feu du serveur (WAF). Veuillez contacter l'administrateur.");
        return false;
      }

      // Mettre à jour l'état local seulement si succès
      set(state => {
        const index = state.profiles.findIndex(p => p.acronym === profile.acronym);
        if (index >= 0) {
          const newProfiles = [...state.profiles];
          newProfiles[index] = profile;
          return { profiles: newProfiles };
        }
        return { profiles: [...state.profiles, profile] };
      });
      return true;
    } catch (e) {
      console.error("Failed to save profile", e);
      alert("Erreur réseau: " + (e as Error).message);
      return false;
    }
  },

  submitProfileModification: async (profile, justification) => {
    const existing = get().profiles.find(p => p.acronym === profile.acronym);
    const updated = { ...profile };
    
    // Éviter l'imbrication exponentielle (previousProfileData dans previousProfileData)
    if (existing) {
      const { previousProfileData, ...existingWithoutPrev } = existing;
      updated.previousProfileData = existingWithoutPrev;
    } else {
      updated.previousProfileData = {};
    }
    
    updated.modificationsCount = (existing?.modificationsCount || 0) + 1;
    updated.lastJustification = justification;
    updated.adminAuthorization = false;
    updated.status = "en_attente";
    updated.submittedAt = new Date().toISOString();
    
    return await get().saveProfile(updated);
  },

  adminValidateProfile: async (acronym, status, adminName, message) => {
    const profile = get().profiles.find(p => p.acronym === acronym);
    if (!profile) return;
    
    const updated = { ...profile };
    updated.status = status;
    updated.validatedAt = new Date().toISOString();
    updated.validatedBy = adminName;
    if (message) {
      updated.adminFeedback = [...(updated.adminFeedback || []), {
        message,
        date: new Date().toISOString(),
        type: status === "valide" ? "validation" : "rejet"
      }];
    }
    await get().saveProfile(updated);
  },

  adminBlockProfileModifications: async (acronym) => {
    const profile = get().profiles.find(p => p.acronym === acronym);
    if (!profile || !profile.previousProfileData) return;
    
    const restored = { ...profile.previousProfileData } as PartyProfile;
    restored.modificationsCount = profile.modificationsCount;
    restored.lastJustification = undefined;
    restored.adminAuthorization = false;
    
    await get().saveProfile(restored);
  },

  adminAuthorizeProfileModification: async (acronym) => {
    const profile = get().profiles.find(p => p.acronym === acronym);
    if (!profile) return;
    
    const updated = { ...profile, adminAuthorization: true };
    await get().saveProfile(updated);
  },

  adminSendProfileMessage: async (acronym, message) => {
    const profile = get().profiles.find(p => p.acronym === acronym);
    if (!profile) return;
    
    const updated = { ...profile };
    updated.adminFeedback = [...(updated.adminFeedback || []), {
      message,
      date: new Date().toISOString(),
      type: "remarque"
    }];
    await get().saveProfile(updated);
  },

  requestProfileUnlock: async (acronym, subject, message) => {
    try {
      const token = localStorage.getItem('kaynin_auth_token');
      const encodedPayload = encodeWafPayload({ action: 'request_unlock', acronym, subject, message });
      
      const res = await fetch(`${getApiUrl()}/parties.php`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'text/plain',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: encodedPayload
      });
      if (!res.ok) {
        const errText = await res.text();
        alert(`Erreur d'envoi: ${res.status} - ${errText}`);
        return;
      }
      await get().fetchProfiles();
    } catch (e) {
      console.error("Failed to request unlock", e);
      alert("Erreur de connexion au serveur.");
    }
  },

  moderateProfileUnlock: async (acronym, status, feedback) => {
    try {
      const token = localStorage.getItem('kaynin_auth_token');
      const encodedPayload = encodeWafPayload({ action: 'moderate_unlock', acronym, status, feedback });

      const res = await fetch(`${getApiUrl()}/parties.php`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'text/plain',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: encodedPayload
      });
      if (!res.ok) {
        const errText = await res.text();
        alert(`Erreur de modération: ${res.status} - ${errText}`);
        return;
      }
      await get().fetchProfiles();
    } catch (e) {
      console.error("Failed to moderate unlock", e);
      alert("Erreur de connexion au serveur.");
    }
  }
}));

const ENABLED_KEY = "kaynin_public_profiles_enabled";
const STRICT_DELEGATE_KEY = "kaynin_strict_delegate_mode";

export function getStrictDelegateMode(): boolean {
  const val = localStorage.getItem(STRICT_DELEGATE_KEY);
  return val ? JSON.parse(val) : false;
}

export async function setStrictDelegateMode(enabled: boolean) {
  localStorage.setItem(STRICT_DELEGATE_KEY, JSON.stringify(enabled));
  await updateSettingOnServer(STRICT_DELEGATE_KEY, enabled);
}

export function getPublicProfilesEnabled(): boolean {
  const val = localStorage.getItem(ENABLED_KEY);
  return val ? JSON.parse(val) : true;
}

export async function setPublicProfilesEnabled(enabled: boolean) {
  localStorage.setItem(ENABLED_KEY, JSON.stringify(enabled));
  await updateSettingOnServer(ENABLED_KEY, enabled);
}

// Retro-compatibility helpers for non-reactive components
export function loadAllPartyProfiles(): PartyProfile[] {
  return usePartiesStore.getState().profiles;
}

export function getPartyProfile(acronym: string): PartyProfile | null {
  return usePartiesStore.getState().profiles.find(p => p.acronym === acronym) || null;
}

export function getValidatedPartyDisplayData(acronym: string, baseStaticName: string, baseStaticFocus: string, lang: 'fr' | 'ar' | 'zgh' = 'fr') {
  const profile = getPartyProfile(acronym);
  const staticData = allPartiesIncludingNonParliamentary.find(p => p.acronym === acronym);

  if (profile && (profile.status === "valide" || profile.status === "en_attente")) {
    let fullName = baseStaticName;
    let focus = baseStaticFocus;
    
    if (lang === 'fr') {
      fullName = profile.fullNameFr || baseStaticName;
      focus = profile.focusFr || baseStaticFocus;
    } else if (lang === 'ar') {
      fullName = profile.fullNameAr || baseStaticName;
      focus = profile.focusAr || baseStaticFocus;
    } else if (lang === 'zgh') {
      fullName = profile.fullNameMz || baseStaticName;
      focus = profile.focusMz || baseStaticFocus;
    }

    return {
      fullName,
      focus,
      logoUrl: profile.logoUrl || staticData?.logoUrl,
      fullPosterUrl: profile.fullPosterUrl,
      poster: profile.poster,
      website: profile.website || staticData?.website,
      socials: { ...staticData?.socials, ...profile.socials },
      jeunesseUrl: profile.jeunesseUrl || staticData?.jeunesseUrl,
      femmesUrl: profile.femmesUrl || staticData?.femmesUrl,
      academieUrl: profile.academieUrl || staticData?.academieUrl,
      adhesionUrl: profile.adhesionUrl,
      partyPrinciples: profile.partyPrinciples || staticData?.partyPrinciples,
      programUrl: profile.programUrl || staticData?.programUrl,
      presentationUrl: profile.presentationUrl || staticData?.presentationUrl,
      programmePdfUrl: profile.programmePdfUrl,
    };
  }

  return {
    fullName: baseStaticName,
    focus: baseStaticFocus,
    logoUrl: staticData?.logoUrl,
    fullPosterUrl: undefined,
    poster: undefined,
    website: staticData?.website,
    socials: staticData?.socials,
    jeunesseUrl: staticData?.jeunesseUrl,
    femmesUrl: staticData?.femmesUrl,
    academieUrl: staticData?.academieUrl,
    adhesionUrl: undefined,
    partyPrinciples: staticData?.partyPrinciples,
    programUrl: staticData?.programUrl,
    presentationUrl: staticData?.presentationUrl,
    programmePdfUrl: undefined,
  };
}

// Redirect old methods to Zustand to avoid breaking code that calls them directly
export const savePartyProfile = (profile: PartyProfile) => usePartiesStore.getState().saveProfile(profile);
export const submitPartyProfileModification = (profile: PartyProfile, justification: string) => usePartiesStore.getState().submitProfileModification(profile, justification);
export const adminValidatePartyProfile = (a: string, s: "valide" | "rejete", n: string, m?: string) => usePartiesStore.getState().adminValidateProfile(a, s, n, m);
export const adminBlockProfileModifications = (a: string) => usePartiesStore.getState().adminBlockProfileModifications(a);
export const adminAuthorizeProfileModification = (a: string) => usePartiesStore.getState().adminAuthorizeProfileModification(a);
export const adminSendProfileMessage = (a: string, m: string) => usePartiesStore.getState().adminSendProfileMessage(a, m);
